/**
 * The run-scope vocabulary — the partition every eval result is filed under.
 *
 * The producer resolves a scope in `EvalRunScope.Resolve`
 * (`tattle.net/src/Intelligence/Tattle.Intelligence.Evaluation.Reporting/EvalRunScope.cs`) and it
 * reaches ADX verbatim, both as the scope path segments of a request and as `summary.runScope`.
 * There are four
 * shapes:
 *
 * - `env=dev` / `env=staging` / `env=prod` — a deployment branch's builds (develop, release/* and
 *   hotfix/*, main).
 * - `pr={id}` — one PR's validation builds.
 * - `branch={branch}` — a topic branch with no PR, or anything CI could not classify.
 * - `email={email}/branch={branch}` — a laptop run. Note the `/`, which is why scope travels as a
 *   query parameter and never as a path segment.
 */

import type { EvaluationRunScopeRef } from './evaluations.types'

/** What a scope's shape says about where the run came from. */
export type EvaluationScopeKind = 'environment' | 'pull-request' | 'branch' | 'local'

export interface EvaluationParsedScope {
  /** The raw scope, exactly as the API and S3 spell it. */
  scope: string
  kind: EvaluationScopeKind
  /** How the scope reads to a human, e.g. `dev`, `PR 5160`, `a@b.com · feature-x`. */
  label: string
  /**
   * Whether the bucket lifecycle expires these results rather than keeping them.
   *
   * Mirrors `TattleResultStore.RootFor` / `EvalRunScope.IsDeployment`: only `env=` is the record of what
   * shipped and stays under `results/`. Everything else is one PR's or one laptop's throwaway run,
   * filed under `results/ephemeral/` and gone in two weeks — so a scope's kind is also how long its
   * executions will still be there to open.
   */
  ephemeral: boolean
}

export const SCOPE_KIND_LABEL: Record<EvaluationScopeKind, string> = {
  environment: 'Environment',
  'pull-request': 'Pull request',
  branch: 'Branch',
  local: 'Local'
}

/** Environments first — they are the kept record; laptop runs last. */
export const SCOPE_KIND_ORDER: EvaluationScopeKind[] = [
  'environment',
  'pull-request',
  'branch',
  'local'
]

/**
 * Which scope a feature page lands on when the URL names none.
 *
 * A **display** default only: it is where most work lands, so it is the most useful thing to show
 * first. It says nothing about what a run is compared against — see `baselineScopeFor`, which
 * never crosses scopes on its own.
 */
export const LANDING_SCOPE = 'env=dev'

const LOCAL_PATTERN = /^email=([^/]+)\/branch=(.+)$/
const ENVIRONMENT_PATTERN = /^env=(.+)$/
const PULL_REQUEST_PATTERN = /^pr=(.+)$/
const BRANCH_PATTERN = /^branch=(.+)$/

/**
 * Reads a scope string into its kind and a human label.
 *
 * Scope strings come out of S3 keys rather than a picker, so an unrecognised one degrades to a
 * branch labelled with the raw value instead of throwing.
 */
export const parseScope = (scope: string): EvaluationParsedScope => {
  const local = LOCAL_PATTERN.exec(scope)
  if (local) {
    return { scope, kind: 'local', label: `${local[1]} · ${local[2]}`, ephemeral: true }
  }

  const environment = ENVIRONMENT_PATTERN.exec(scope)
  if (environment) {
    return { scope, kind: 'environment', label: environment[1], ephemeral: false }
  }

  const pullRequest = PULL_REQUEST_PATTERN.exec(scope)
  if (pullRequest) {
    return { scope, kind: 'pull-request', label: `PR ${pullRequest[1]}`, ephemeral: true }
  }

  const branch = BRANCH_PATTERN.exec(scope)
  return { scope, kind: 'branch', label: branch ? branch[1] : scope, ephemeral: true }
}

/**
 * A scope is one or more path segments, and the route carries it as such so a URL translates
 * straight to an S3 key:
 * `results{/ephemeral}/{feature}/{scope}/{execution}/{scenario}/{iteration}.json` — `results/` for
 * an `env=` scope, `results/ephemeral/` for every other, per `TattleResultStore.RootFor`.
 *
 * A laptop scope's internal `/` therefore has to stay a real separator. vue-router's `encodeParam`
 * turns `/` into `%2F` inside a single param, so the route declares `:scope+` (repeatable) and
 * these two translate at that boundary.
 */
export const scopeToSegments = (scope: string): string[] => (scope ? scope.split('/') : [])

/** vue-router hands back a bare string when a repeatable param matched exactly one segment. */
export const scopeFromSegments = (segments: string | string[] | undefined): string =>
  Array.isArray(segments) ? segments.join('/') : (segments ?? '')

/**
 * The scope to show when the route names none, or names one this feature has no results under.
 *
 * Falls back to whichever scope ran most recently when there is no `LANDING_SCOPE`. Empty when the
 * feature has no scopes at all.
 */
export const defaultScopeFor = (scopes: EvaluationRunScopeRef[]): string => {
  if (scopes.some(scope => scope.scope === LANDING_SCOPE)) {
    return LANDING_SCOPE
  }

  // Execution names are sortable UTC timestamps, so a string compare is a recency compare.
  const newest = [...scopes].sort((left, right) =>
    (right.latestExecution ?? '').localeCompare(left.latestExecution ?? '')
  )[0]

  return newest?.scope ?? ''
}

/**
 * The scope named by a route query, or the default when it names none or names one that is not
 * there — a stale link must not silently read a different scope's run.
 */
export const resolveScope = (requestedScope: unknown, scopes: EvaluationRunScopeRef[]): string => {
  const requested = typeof requestedScope === 'string' ? requestedScope : null

  return requested && scopes.some(scope => scope.scope === requested)
    ? requested
    : defaultScopeFor(scopes)
}

/**
 * The scope this execution's comparison should default to.
 *
 * Mirrors the .NET report's baseline column — `EvalRunScope.ResolveBaseline` picks the scope and
 * `EvalFeature.AppendBaselineAsync` appends that scope's newest execution:
 *
 * 1. What the run itself recorded, when that scope still has results. The only exact-parity path,
 *    and the reason the producer stamps `summary.baselineScope`.
 * 2. Otherwise the run's own scope, for every kind — the previous execution of the same scope,
 *    which is what the report's own two same-scope columns show
 *    (`EvalFeature.ExecutionsInReport` is 2).
 *
 * There is deliberately no cross-scope guess. `ResolveBaseline` returns null unless the run is a CI
 * build with `BITBUCKET_PR_ID` set, so a topic-branch or laptop run gets no baseline column at all;
 * and even for a PR build the scope is `ForBranch(BITBUCKET_PR_DESTINATION_BRANCH)` — `env=dev` only
 * when the destination is `develop`, `env=staging` off `release/*` or `hotfix/*`, `env=prod` off
 * `main`. The destination branch is not recorded in the S3 key, so a summary written before
 * `baselineScope` existed gets no cross-scope default: ADX cannot know what it targeted, and
 * guessing would show a comparison .NET never made.
 *
 * The user can pick another scope in the Comparison tab — that is the escape hatch for those older
 * summaries, and it is an explicit choice rather than a default.
 */
export const baselineScopeFor = (
  currentScope: string,
  availableScopes: string[],
  statedBaselineScope?: string | null
): string =>
  statedBaselineScope && availableScopes.includes(statedBaselineScope)
    ? statedBaselineScope
    : currentScope
